import {
  forwardRef,
  useId,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cx } from './cx';

export interface C1TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  id?: string;
  label: ReactNode;
  error?: ReactNode;
  hint?: ReactNode;
}

/** Floating-label textarea using the same C1 field chrome. */
export const C1Textarea = forwardRef<HTMLTextAreaElement, C1TextareaProps>(function C1Textarea(
  { id, label, error, hint, className, value, defaultValue, ...props },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  const invalid = Boolean(error);
  const hasValue =
    (typeof value === 'string' && value.length > 0) ||
    (typeof defaultValue === 'string' && defaultValue.length > 0);

  return (
    <div>
      <div
        className={cx(
          'c1-field',
          'c1-field--textarea',
          hasValue && 'c1-field--floated',
          invalid && 'c1-field-invalid',
          className,
        )}
      >
        <textarea
          ref={ref}
          id={fieldId}
          className="c1-textarea"
          placeholder=" "
          value={value}
          defaultValue={defaultValue}
          aria-invalid={invalid || undefined}
          aria-describedby={
            [invalid ? errorId : undefined, hint && !invalid ? hintId : undefined]
              .filter(Boolean)
              .join(' ') || undefined
          }
          {...props}
        />
        <label className="c1-label" htmlFor={fieldId}>
          {label}
        </label>
      </div>
      {error ? (
        <p className="c1-field-error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
      {hint && !error ? (
        <p className="c1-otp-hint" id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
});
